import { useState, useEffect, useRef } from 'react';
import { reviewService, Review } from '../services/reviewService';

/**
 * Hook to manage review submission and polling
 * Wraps the existing reviewService with React state management
 */
export function useReviewManagement(
  projectId: string | null,
  activeSection: string,
  onSectionComplete: (section: 'review') => void
) {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [pollingReviewId, setPollingReviewId] = useState<string | null>(null);
  const pollingReviewIdRef = useRef<string | null>(null);

  const fetchReviews = async () => {
    if (!projectId) return;
    
    setLoadingReviews(true);
    try {
      const fetchedReviews = await reviewService.getGameReviews(projectId);
      setReviews(fetchedReviews || []);
      console.log('Loaded reviews:', fetchedReviews?.length || 0);
      
      // Check if there's an active review and start polling
      const activeReview = fetchedReviews?.find(
        r => r.status === 'in_progress' || r.status === 'pending'
      );
      if (activeReview) {
        startReviewPolling(activeReview.id);
      }
      
      // Update review section completion status if there's a passed or published review
      const hasPassedReview = fetchedReviews?.some(
        r => r.status === 'passed' || r.status === 'published'
      );
      if (hasPassedReview) {
        onSectionComplete('review');
        console.log('✅ Review section marked as complete (passed review found)');
      }
    } catch (err) {
      console.error('Failed to fetch reviews:', err);
      setReviews([]);
    } finally {
      setLoadingReviews(false);
    }
  };

  const startReviewPolling = (reviewId: string) => {
    // Use ref for immediate check to prevent race conditions
    if (pollingReviewIdRef.current === reviewId) {
      console.log('🔄 Already polling review', reviewId, '- skipping duplicate call');
      return;
    }
    
    console.log('🔄 Starting review polling for', reviewId);
    pollingReviewIdRef.current = reviewId;
    setPollingReviewId(reviewId);
    
    reviewService.pollReviewStatus(
      reviewId,
      (updatedReview) => {
        console.log('Review update:', updatedReview.status, updatedReview.progress);
        setReviews(prev => prev.map(r => r.id === reviewId ? updatedReview : r));
      },
      5000 // Poll every 5 seconds
    ).then((completedReview) => {
      console.log('✅ Review polling complete:', completedReview.status);
      pollingReviewIdRef.current = null;
      setPollingReviewId(null);
      
      if (completedReview.status === 'passed' || completedReview.status === 'published') {
        onSectionComplete('review');
      }
    }).catch((error) => {
      console.error('Review polling error:', error);
      pollingReviewIdRef.current = null;
      setPollingReviewId(null);
    });
  };

  const handleSubmitReview = async (
    editorVersionId: string,
    onSuccess: (message: string) => void,
    onError: (message: string) => void
  ) => {
    if (!projectId || isSubmittingReview) return;
    
    setIsSubmittingReview(true);
    try {
      const response = await reviewService.submitForReview(projectId, editorVersionId);
      console.log('Review submitted:', response.review);
      
      // Add new review to state
      setReviews(prev => [response.review, ...prev]);
      
      // Start polling for this review
      startReviewPolling(response.review.id);
      
      onSuccess('Version submitted for review successfully!');
    } catch (err: any) {
      console.error('Failed to submit review:', err);
      const errorMsg = err.response?.data?.error || err.response?.data?.message || 'Failed to submit review';
      onError(errorMsg);
    } finally {
      setIsSubmittingReview(false);
    }
  };

  // Fetch reviews when project loads
  useEffect(() => {
    if (projectId) {
      fetchReviews();
    }
  }, [projectId]);

  // Fetch reviews when review section is opened
  useEffect(() => {
    if (activeSection === 'review' && projectId) {
      fetchReviews();
    }
  }, [activeSection, projectId]);

  return {
    reviews,
    loadingReviews,
    isSubmittingReview,
    pollingReviewId,
    handleSubmitReview,
    fetchReviews
  };
}

